import {
  ApolloClient,
  createHttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { datadogRum } from "@datadog/browser-rum";
import { useMemo } from "react";

import { possibleTypes } from "./graphql/possibleTypes.json";
import { CaptchaContextValue, useCaptcha } from "./providers/CaptchaProvider";
import { useTenantSlug } from "./providers/TenantSlugProvider";

export default function client(
  tenantSlug: string,
  captcha: CaptchaContextValue
): ApolloClient<NormalizedCacheObject> {
  const httpLink = createHttpLink({
    uri: "/graphql",
    headers: {
      accept: "application/json",
    },
  });

  const datadogErrorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      const errors = [];
      if (Array.isArray(graphQLErrors)) {
        errors.push(...graphQLErrors);
      } else {
        errors.push(graphQLErrors);
      }

      errors.forEach(({ message, locations, path, extensions }) => {
        if (extensions?.code === "INTERNAL_SERVER_ERROR") {
          datadogRum.addError(new Error(message), {
            extra: { locations, path, extensions },
          });
        }
      });
    }
    if (networkError) {
      if ("statusCode" in networkError && networkError.statusCode === 401) {
        datadogRum.addAction("session_expired_401");
      } else {
        console.error(networkError);
      }
    }
  });

  let captchaInProgress = false;
  const wafChallengeLink = onError(({ networkError, operation, forward }) => {
    if (
      networkError &&
      "response" in networkError &&
      networkError.response.status === 405
    ) {
      if (captchaInProgress) {
        console.warn("WAF challenge already in progress; ignoring");
        return;
      }
      captchaInProgress = true;
      console.warn("WAF challenge detected; showing captcha");
      return new Observable(observer => {
        captcha.showCaptcha(token => {
          captchaInProgress = false;
          operation.setContext(({ headers = {} }) => ({
            headers: {
              ...headers,
              "x-aws-waf-token": token,
            },
          }));
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };
          // Retry last failed request
          forward(operation).subscribe(subscriber);
        });
      });
    }
  });

  return new ApolloClient({
    link: datadogErrorLink.concat(wafChallengeLink).concat(httpLink),
    cache: new InMemoryCache({
      typePolicies: {
        StaffEmail: {
          keyFields: ["email"],
        },
        ProviderRateLimit: {
          keyFields: ["providerRateLimitKey"],
        },
        LaneRateLimit: {
          keyFields: ["providerRateLimitKey", "lane"],
        },
        LaneUsage: {
          keyFields: ["providerRateLimitKey", "lane", "timestamp"],
        },
      },
      possibleTypes,
    }),
  });
}

export function useTenantApolloClient(): ApolloClient<NormalizedCacheObject> {
  const tenantSlug = useTenantSlug();
  const captcha = useCaptcha();

  return useMemo(() => {
    return client(tenantSlug, captcha);
  }, [tenantSlug, captcha]);
}
